/**
 * POS Permission catalog — the subset of Security_lkp_Permissions the
 * cashier app cares about.
 *
 * SOURCE OF TRUTH IS THE NAME, NOT THE ID.
 *   Across tenant databases the numeric Security_lkp_Permissions.PermissionID
 *   is NOT stable — the same logical permission ("Edit Order") can have a
 *   different PermissionID in each tenant. PermissionNameEn, however, is
 *   consistent. The legacy POS therefore authorizes by name
 *   (UserHelper.IsAuthorized matches PermissionNameEn case-insensitively),
 *   and so do we.
 *
 *   The numeric IDs below are kept only as a *canonical* convenience for the
 *   few backend gates that still consult `permissions.codes`. The backend
 *   resolves a user's permissions by walking the POS module tree and reading
 *   PermissionNameEn, then maps those names back to these canonical IDs — so
 *   `codes` is tenant-stable even though raw tenant PermissionIDs are not.
 *
 * Why a curated subset (and not "all permissions a user has"):
 *   The legacy ERP has thousands of permission rows (HR, contracts, fleet,
 *   etc.). The JWT only carries POS-module permission names, which is small.
 *
 * Adding a new permission:
 *   1. Ensure the row exists in Security_lkp_Permissions under the POS module
 *      (Security_Modules.ModuleNameEn = 'POS') in the back-office.
 *   2. Add a PERM_* const + its PermissionNameEn here.
 *   3. Add it to POS_PERMISSION_CATALOG.
 *   4. Use POS_PERMISSIONS.YourNew in frontend gates.
 */

// ── POS Order/Receipt permissions ────────────────────────────────────────

/** Create a new POS order. Default for every cashier; here for completeness. */
export const PERM_CREATE_ORDER = 185;

/** Cancel a single line in the cart before checkout. */
export const PERM_CANCEL_ITEM = 376;

/** Cancel the whole open order (clear cart, no receipt written). */
export const PERM_CANCEL_ORDER = 377;

/** Edit an EXISTING saved receipt (the focus of Phase 5d). */
export const PERM_EDIT_ORDER = 385;

/** Override the unit price of a line item in the cart. */
export const PERM_EDIT_CART_ITEM_PRICE = 390;

/** Create a credit-customer order (on-account sale). */
export const PERM_CREATE_CREDIT_ORDER = 7668;

/** Create a credit-customer return. */
export const PERM_CREATE_CREDIT_RETURN = 7670;

/** Change the payment-method tag on the receipt header. */
export const PERM_CHANGE_PAYMENT_METHOD = 8821;

/** Change the salesman attached to the order. */
export const PERM_CHANGE_SALESMAN = 8997;

/** Change the order type (dine-in/takeaway/delivery). */
export const PERM_CHANGE_ORDER_TYPE = 9062;

/**
 * Edit the salesman on a receipt that's already been posted/closed.
 * More sensitive than PERM_CHANGE_SALESMAN — that one applies during cart
 * entry; this rewrites a closed transaction.
 */
export const PERM_CHANGE_SALESMAN_IN_CLOSED_ORDERS = 12151;

// ── Call Center permissions (migration 046) ──────────────────────────────
// Name-only gates, like the "Not Allow ..." family: the rows are seeded by
// migration 046 with an IDENTITY PermissionID, so no canonical id exists and
// none is needed — `permission_names` is what the JWT carries and what the
// backend and the frontends both match on.

/**
 * Holding this routes the user to the Call Center screen at sign-in instead
 * of the till, and unlocks every /api/v1/call-center agent endpoint.
 * WITHOUT it the app behaves exactly as it did before this feature existed.
 */
export const PERM_CALL_CENTER = 0;

/**
 * See the whole subsidiary's call-center traffic, not just orders this agent
 * took. Separated from PERM_CALL_CENTER because a floor agent taking calls and
 * a supervisor watching every branch are different jobs.
 */
export const PERM_CALL_CENTER_DASHBOARD = 0;

// ── Inventory ────────────────────────────────────────────────────────────

/**
 * Store-to-store transfers (the legacy POS "Transfer" menu entry, Code 80 →
 * 81). Name-only: the row hangs under POS → Orders on every tenant, seeded
 * long ago by the ERP, so no canonical id is needed. Holding it shows the
 * Transfers workspace and unlocks /api/v1/inventory-transfers.
 */
export const PERM_CREATE_TRANSFER_TRANSACTION = 0;

// ── POS Reports permissions ──────────────────────────────────────────────

/** View the order-history list across the user's branch. */
export const PERM_VIEW_ORDER_HISTORY = 188;

/** View parked orders globally (not just one's own). */
export const PERM_VIEW_HOLDED_ORDERS = 543;

/** View ALL branches' parked orders (not just current user's branch). */
export const PERM_VIEW_ALL_BRANCH_HOLDED_ORDERS = 544;

/** View ALL branches' orders (multi-branch report). */
export const PERM_VIEW_ALL_BRANCH_ORDERS = 545;

/** View a single customer's order history. */
export const PERM_VIEW_CUSTOMER_ORDERS = 547;

/** View the salesman-summary report. */
export const PERM_VIEW_SALES_BY_SALESMAN = 12156;

/**
 * Report view permissions. The NAME is authoritative (matched against the
 * user's permission_names); the ids below are placeholders (0) because the
 * canonical PermissionID isn't stable/known for these — gating is name-only,
 * exactly like the "Not Allow ..." gates. The names mirror the legacy POS
 * permission strings verbatim (including the "Bunsiness" typo and the
 * "Cashier Sales Report" name used for the Item Details report).
 */
export const PERM_VIEW_QUICK_SALES = 0;
export const PERM_VIEW_BUSINESS_SUMMARY = 0;
export const PERM_VIEW_ITEM_DETAILS = 0;

// ── Order-type gating (legacy "Not Allow ..." permissions) ───────────────
// In the legacy POS these are *negative* gates: holding the permission BLOCKS
// the cashier from using that order type. We model them as negative too (see
// useCannot in the cashier app). IDs are placeholders for the canonical map;
// the NAME is authoritative.

/** Holding this BLOCKS in-shop (walk-in) ordering. */
export const PERM_NOT_ALLOW_IN_SHOP = 0;
/** Holding this BLOCKS take-away ordering. */
export const PERM_NOT_ALLOW_TAKE_AWAY = 0;
/** Holding this BLOCKS delivery ordering. */
export const PERM_NOT_ALLOW_DELIVERY = 0;
/** Holding this BLOCKS dine-in ordering. */
export const PERM_NOT_ALLOW_DINE_IN = 0;
/** Holding this BLOCKS pickup ordering. */
export const PERM_NOT_ALLOW_PICKUP = 0;
/** Holding this BLOCKS membership ordering. */
export const PERM_NOT_ALLOW_MEMBERSHIP = 0;

// ── Canonical catalog: { key, id, name } ─────────────────────────────────

/**
 * Every POS permission the frontend may reference, with its canonical
 * numeric id and its tenant-stable PermissionNameEn. The NAME is what flows
 * through the JWT (`permissions.permission_names`) and what gates check.
 */
export const POS_PERMISSION_CATALOG = [
  { key: 'CreateOrder', id: PERM_CREATE_ORDER, name: 'Create Order' },
  { key: 'CancelItem', id: PERM_CANCEL_ITEM, name: 'Cancel Item' },
  { key: 'CancelOrder', id: PERM_CANCEL_ORDER, name: 'Cancel Order' },
  { key: 'EditOrder', id: PERM_EDIT_ORDER, name: 'Edit Order' },
  { key: 'EditCartItemPrice', id: PERM_EDIT_CART_ITEM_PRICE, name: 'Edit Cart Item Price' },
  { key: 'CreateCreditOrder', id: PERM_CREATE_CREDIT_ORDER, name: 'Create Credit Order' },
  { key: 'CreateCreditReturn', id: PERM_CREATE_CREDIT_RETURN, name: 'Create Credit Return' },
  { key: 'ChangePaymentMethod', id: PERM_CHANGE_PAYMENT_METHOD, name: 'Change payment method' },
  { key: 'ChangeSalesman', id: PERM_CHANGE_SALESMAN, name: 'Change Salesman' },
  { key: 'ChangeOrderType', id: PERM_CHANGE_ORDER_TYPE, name: 'Change order type' },
  { key: 'ChangeSalesmanInClosedOrders', id: PERM_CHANGE_SALESMAN_IN_CLOSED_ORDERS, name: 'Change salesman in closed orders' },
  { key: 'CallCenter', id: PERM_CALL_CENTER, name: 'Call Center' },
  { key: 'CallCenterDashboard', id: PERM_CALL_CENTER_DASHBOARD, name: 'Call Center Dashboard' },
  { key: 'CreateTransferTransaction', id: PERM_CREATE_TRANSFER_TRANSACTION, name: 'Create Transfer Transaction' },
  // The legacy Delivery Manager screen and its return gate. Name-only (id 0):
  // 'Delivery Manger' is the ERP's own spelling, typo and all.
  { key: 'DeliveryManager', id: 0, name: 'Delivery Manger' },
  { key: 'CreateCashReturn', id: 0, name: 'Create Cash Return' },
  { key: 'ViewOrderHistory', id: PERM_VIEW_ORDER_HISTORY, name: 'Order History' },
  { key: 'ViewHoldedOrders', id: PERM_VIEW_HOLDED_ORDERS, name: 'Holded Orders' },
  { key: 'ViewAllBranchHoldedOrders', id: PERM_VIEW_ALL_BRANCH_HOLDED_ORDERS, name: 'View All Branch Holded Orders' },
  { key: 'ViewAllBranchOrders', id: PERM_VIEW_ALL_BRANCH_ORDERS, name: 'View All Branch Orders' },
  { key: 'ViewCustomerOrders', id: PERM_VIEW_CUSTOMER_ORDERS, name: 'Customer Orders Report' },
  { key: 'ViewSalesBySalesman', id: PERM_VIEW_SALES_BY_SALESMAN, name: 'Sales By Salesman Summary Report' },
  { key: 'ViewQuickSales', id: PERM_VIEW_QUICK_SALES, name: 'Quick Sales Report' },
  { key: 'ViewBusinessSummary', id: PERM_VIEW_BUSINESS_SUMMARY, name: 'Bunsiness Summary' },
  { key: 'ViewItemDetails', id: PERM_VIEW_ITEM_DETAILS, name: 'Cashier Sales Report' },
  { key: 'NotAllowInShop', id: PERM_NOT_ALLOW_IN_SHOP, name: 'Not Allow In shop' },
  { key: 'NotAllowTakeAway', id: PERM_NOT_ALLOW_TAKE_AWAY, name: 'Not Allow Take Away' },
  { key: 'NotAllowDelivery', id: PERM_NOT_ALLOW_DELIVERY, name: 'Not Allow Delivery' },
  { key: 'NotAllowDineIn', id: PERM_NOT_ALLOW_DINE_IN, name: 'Not Allow Dine In' },
  { key: 'NotAllowPickup', id: PERM_NOT_ALLOW_PICKUP, name: 'Not Allow Pickup' },
  { key: 'NotAllowMembership', id: PERM_NOT_ALLOW_MEMBERSHIP, name: 'Not Allow Membership' },
  // The legacy dine-in table screen and its Separate / Transfer dialog.
  // Name-only (id 0); present under POS on every tenant probed, matched
  // case-insensitively (the fleet carries both "join" and "Join").
  { key: 'ChangeTables', id: 0, name: 'Change tables on dine-in' },
  { key: 'MoveTables', id: 0, name: 'Move tables on dine-in' },
  { key: 'JoinTables', id: 0, name: 'Join tables on dine-in' },
  { key: 'TransferTableItems', id: 0, name: 'Transfer items between Dine-In tables' },
  { key: 'SeparateTableOrders', id: 0, name: 'Separate orders on dine-in' },
] as const;

/**
 * The full set of canonical IDs the backend may embed in `permissions.codes`.
 * (Order-type "Not Allow" gates have no canonical id and are name-only.)
 */
export const POS_PERMISSION_IDS: readonly number[] = POS_PERMISSION_CATALOG
  .map((p) => p.id)
  .filter((id) => id > 0);

/**
 * Key → PermissionNameEn. Frontend gates use POS_PERMISSIONS.EditOrder which
 * is the *name string* ('Edit Order'), matched case-insensitively against the
 * JWT's permission_names.
 */
export const POS_PERMISSIONS = Object.fromEntries(
  POS_PERMISSION_CATALOG.map((p) => [p.key, p.name]),
) as { [P in (typeof POS_PERMISSION_CATALOG)[number]['key']]: string };

/** name (lowercased, trimmed) → canonical id, for back-compat code paths. */
export const PERM_NAME_TO_ID: Readonly<Record<string, number>> = Object.fromEntries(
  POS_PERMISSION_CATALOG.filter((p) => p.id > 0).map((p) => [p.name.trim().toLowerCase(), p.id]),
);

export type PosPermissionKey = (typeof POS_PERMISSION_CATALOG)[number]['key'];
/** A tenant-stable PermissionNameEn string. */
export type PosPermissionName = string;
/** @deprecated numeric ids are no longer the gating key; use PosPermissionName. */
export type PosPermissionId = number;
